import { AnimatePresence } from 'framer-motion'
import React from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { TextTooltip } from '../InfoPopup'
import { FaQuestionCircle } from 'react-icons/fa'
import cn from 'classnames'

interface Props {
  suggestionDisplayName: string
  label: string
  key: string
  icon: React.ReactNode
  toolTip?: string
  dismissComponent?: React.ReactNode
  to?: string
}

export const AiSuggestion: React.FC<Props> = ({
  suggestionDisplayName,
  label,
  key,
  icon: Icon,
  toolTip,
  dismissComponent,
  to
}) => {
  const isLink = !!to

  const card = (
    <SuggestionCard className={cn({ isLink })}>
      <SuggestionIconWrapper>
        <SuggestionIcon>{Icon}</SuggestionIcon>
      </SuggestionIconWrapper>
      <SuggestionText>
        <SuggestionLabel>{label}</SuggestionLabel>
        <SuggestionName>{suggestionDisplayName}</SuggestionName>
      </SuggestionText>
      {toolTip && (
        <ToolTipContainer>
          <TextTooltip tooltip={toolTip}>
            <FaQuestionCircle />
          </TextTooltip>
        </ToolTipContainer>
      )}
      {dismissComponent}
    </SuggestionCard>
  )

  return (
    <AnimatePresence key={key}>
      {to ? <StyledLink to={to}>{card}</StyledLink> : card}
    </AnimatePresence>
  )
}

const SuggestionCard = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  overflow: hidden;
  position: relative;

  text-decoration: none;

  &.isLink {
    cursor: pointer;
  }
`

const SuggestionIconWrapper = styled.div`
  margin-right: ${({ theme }) => theme.spacing.sm}rem;
`

const SuggestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.iconSize.md}rem;
  height: ${({ theme }) => theme.iconSize.md}rem;
  border-radius: 50%;

  background: ${({ theme }) => theme.colors.AIPurple}11;
  color: ${({ theme }) => theme.colors.AIPurple};
  box-shadow: none;
`

const SuggestionText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const SuggestionLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const SuggestionName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.AIPurple};
  display: inline-block;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -2px;
    left: 0;
    background-color: ${({ theme }) => theme.colors.AIPurple};
    transition: all 0.3s ease;
  }

  ${SuggestionCard}.neutral & {
    color: ${({ theme }) => theme.colors.neutralBlack};
  }
`

const ToolTipContainer = styled.div`
  margin-left: auto;
  position: absolute;
  top: ${({ theme }) => theme.spacing.xs}rem;
  right: ${({ theme }) => theme.spacing.sm}rem;

  & > * {
    color: ${({ theme }) => theme.colors.metalGray};
  }
`

const StyledLink = styled(Link)`
  text-decoration: none;
  display: block;
  color: inherit;

  &:hover {
    text-decoration: none;
    color: inherit;

    ${SuggestionName} {
      &:after {
        width: 100%;
      }
    }
  }

  &:visited,
  &:active,
  &:link {
    text-decoration: none;
    color: inherit;
  }
`
